import { useState, useMemo } from 'react';
import remove from 'lodash.remove';
import {
    R3D,
    DIBELS8,
    BURST,
    R3DSPANISH,
    IDEL2,
    READING,
    ESPANOL,
} from '../../constants/applications';
import { COLUMNS_NAMES, SORTING_ORDER, sortStudents } from '../utils/studentsSortingHelpers';

/**
 * Returns inverse value of provided sorting order
 * Returns 'asc' if no order provided
 * @param {string} order - sorting order ('asc', 'desc')
 * @returns {string}
 */
export const swapOrder = order =>
    order === SORTING_ORDER.ASC ? SORTING_ORDER.DESC : SORTING_ORDER.ASC;

/**
 * Get parameters for students list sorting on Reading app group
 * depending on licensed applications.
 * Sorting params include column name and sorting order (ascending by default).
 * Initial order of sorting colums is: D8, BURST, TRC, NAME.
 * Some of those columns may be skipped, depending on licensed applications.
 * If no licensed applications, then only sorting by student's name is available.
 * First item in this list defines primary sorting column.
 *
 * @param {Array<String>} applications list of licensed applications
 * @returns {Array<Object>} sorting parameters
 */
const getReadingSortingParams = (applications = []) => {
    // sorting params array is filled in reversed order with unshift method: Name -> TRC -> BURST -> D8
    const sortingParams = [{ column: COLUMNS_NAMES.NAME, order: SORTING_ORDER.ASC }];
    if (applications.includes(R3D)) {
        sortingParams.unshift({ column: COLUMNS_NAMES.TRC, order: SORTING_ORDER.ASC });
    }

    if (applications.includes(BURST)) {
        sortingParams.unshift({
            column: COLUMNS_NAMES.INTERVENTION,
            order: SORTING_ORDER.ASC,
        });
    }

    if (applications.includes(DIBELS8)) {
        sortingParams.unshift({ column: COLUMNS_NAMES.DIBELS, order: SORTING_ORDER.ASC });
    }
    return sortingParams;
};

/**
 * Get parameters for students list sorting on Reading app group
 * depending on licensed applications.
 * Sorting params include column name and sorting order (ascending by default).
 * Initial order of sorting colums is: IDEL2(Lectura) -> TRC -> Name
 * Some of those columns may be skipped, depending on licensed applications.
 * If no licensed applications, then only sorting by student's name is available.
 * First item in this list defines primary sorting column.
 *
 * @param {Array<String>} applications list of licensed applications
 * @returns {Array<Object>} sorting parameters
 */
const getSpanishSortingParams = (applications = []) => {
    // sorting params array is filled in reversed order with unshift method: Name -> TRC -> IDEL2(Lectura)
    const sortingParams = [{ column: COLUMNS_NAMES.NAME, order: SORTING_ORDER.ASC }];
    if (applications.includes(R3DSPANISH)) {
        sortingParams.unshift({ column: COLUMNS_NAMES.TRC, order: SORTING_ORDER.ASC });
    }

    if (applications.includes(IDEL2)) {
        sortingParams.unshift({ column: COLUMNS_NAMES.IDEL2, order: SORTING_ORDER.ASC });
    }
    return sortingParams;
};

const useSortedStudents = (students, applications, selectedAppGroup, period) => {
    const [sortingParamsGroups, setSortingParamsGroups] = useState(() => ({
        [READING]: getReadingSortingParams(applications),
        [ESPANOL]: getSpanishSortingParams(applications),
    }));
    const sortingParams = sortingParamsGroups[selectedAppGroup] ?? [];

    const sortedStudents = useMemo(
        () => sortStudents(students, sortingParams, period, selectedAppGroup),
        [period, selectedAppGroup, sortingParams, students],
    );

    // After the primary sorting field changes (click on column's header), we need to
    // put that field with correct order to the beginning of the sortingParams array.
    const changeSortingOrder = columnName => {
        const newSortingParams = [...sortingParams];
        const leadingColumnName = sortingParams[0] && sortingParams[0].column;
        const [columnSortingParams] = remove(
            newSortingParams,
            param => param.column === columnName,
        );
        if (columnSortingParams) {
            newSortingParams.unshift({
                column: columnName,
                order:
                    leadingColumnName === columnName
                        ? swapOrder(columnSortingParams.order)
                        : SORTING_ORDER.ASC,
            });

            setSortingParamsGroups(prevParams => ({
                ...prevParams,
                [selectedAppGroup]: newSortingParams,
            }));
        }
    };

    return {
        sortingParams,
        changeSortingOrder,
        sortedStudents,
    };
};

export default useSortedStudents;
